import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Search, AlertCircle } from "lucide-react";
import ReleaseCard from "../components/ReleaseCard";
import { toast } from "sonner";

export default function MissingPage() {
  const navigate = useNavigate();
  const [releases, setReleases] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("value_high");
  const [loading, setLoading] = useState(true);

  const fetchMissing = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: "24", sort, missing_only: "true" });
      if (search) params.set("search", search);
      const { data } = await api.get(`/releases?${params.toString()}`);
      setReleases(data.releases);
      setTotal(data.total);
      setPages(data.pages);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, [page, search, sort]);

  useEffect(() => { fetchMissing(); }, [fetchMissing]);

  const handleAddCollection = async (release) => {
    try {
      await api.post("/collection", { release_id: release.release_id });
      toast.success("Added to collection!");
      fetchMissing();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const handleAddWishlist = async (release) => {
    try {
      await api.post("/wishlist", { release_id: release.release_id });
      toast.success("Added to wishlist!");
      fetchMissing();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const missingReleases = useMemo(() =>
    releases.map((r) => ({ ...r, owned: false })),
    [releases]
  );

  return (
    <div data-testid="missing-page" className="p-4 sm:p-6 lg:p-8 space-y-6 animate-fadeIn">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-zinc-100 tracking-tight flex items-center gap-3">
          <AlertCircle className="w-8 h-8 text-amber-400" /> Missing Releases
        </h1>
        <p className="text-zinc-500 mt-1 text-sm">{total} releases you don&apos;t own yet</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            data-testid="missing-search"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search missing releases..."
            className="pl-10 bg-zinc-900 border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus:border-rose-500"
          />
        </div>
        <Select value={sort} onValueChange={(v) => { setSort(v); setPage(1); }}>
          <SelectTrigger className="w-[160px] bg-zinc-900 border-zinc-800 text-zinc-300">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="value_high">Highest Value</SelectItem>
            <SelectItem value="newest">Newest</SelectItem>
            <SelectItem value="chase_first">Chase First</SelectItem>
            <SelectItem value="alpha">A-Z</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
          {Array.from({ length: 12 }, (_, i) => `missing-skel-${i}`).map((id) => (
            <div key={id} className="bg-zinc-900 rounded-xl overflow-hidden animate-pulse">
              <div className="aspect-[4/3] bg-zinc-800" />
              <div className="p-3 space-y-2">
                <div className="h-3 bg-zinc-800 rounded w-2/3" />
                <div className="h-4 bg-zinc-800 rounded" />
              </div>
            </div>
          ))}
        </div>
      ) : releases.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-emerald-400 text-lg font-heading font-semibold">You own everything!</p>
          <p className="text-zinc-500 text-sm mt-1">Complete collection achieved</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
          {missingReleases.map((r) => (
            <ReleaseCard
              key={r.release_id}
              release={r}
              isMissing
              onAddCollection={handleAddCollection}
              onAddWishlist={handleAddWishlist}
              onCardClick={(id) => navigate(`/catalog/${id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
